import {
  formatarDocumento,
  type CadastroTinyDTO,
  type ContatoTinyResumoDTO,
  type LinkCadastroDTO,
} from '@viniko/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Link2, RefreshCw, Search, Unlink } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { colarNaMensagem } from '../lib/cotacao';
import { Modal, useToast } from './ui';
import './cadastro-tiny.css';

const STATUS = {
  vinculado: { rotulo: 'Vinculado', classe: 'ok' },
  incompleto: { rotulo: 'Vinculado, incompleto', classe: 'atencao' },
  novo: { rotulo: 'Cliente novo', classe: 'novo' },
} as const;

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const textoDoLink = (url: string) =>
  `Pra gente emitir o pedido e a nota fiscal, preencha seu cadastro neste link (leva uns 2 minutos):\n${url}`;

export function CadastroTiny({
  contatoId,
  conversaId,
  aoColar,
}: {
  contatoId: string;
  conversaId?: string;
  aoColar?: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const chave = ['tiny', contatoId];
  const cadastro = useQuery({
    queryKey: chave,
    queryFn: () => api<CadastroTinyDTO>(`/api/contatos/${contatoId}/tiny`),
    staleTime: 60_000,
    retry: false,
  });
  const [buscando, setBuscando] = useState(false);
  const [atualizando, setAtualizando] = useState(false);
  const [gerando, setGerando] = useState(false);

  async function gerarLink() {
    if (!conversaId) return;
    setGerando(true);
    try {
      const l = await api<LinkCadastroDTO>(`/api/conversas/${conversaId}/cadastro`, {
        metodo: 'POST',
      });
      colarNaMensagem(conversaId, textoDoLink(l.url ?? window.location.origin + l.caminho));
      qc.invalidateQueries({ queryKey: chave });
      aoColar?.();
    } catch (e) {
      toast((e as Error).message, 'erro');
    } finally {
      setGerando(false);
    }
  }

  async function atualizar() {
    setAtualizando(true);
    try {
      qc.setQueryData(
        chave,
        await api<CadastroTinyDTO>(`/api/contatos/${contatoId}/tiny?atualizar=1`),
      );
    } catch (e) {
      toast((e as Error).message, 'erro');
    } finally {
      setAtualizando(false);
    }
  }

  async function desligar() {
    try {
      qc.setQueryData(
        chave,
        await api<CadastroTinyDTO>(`/api/contatos/${contatoId}/tiny`, { metodo: 'DELETE' }),
      );
      toast('Contato desligado do Tiny');
    } catch (e) {
      toast((e as Error).message, 'erro');
    }
  }

  if (cadastro.isLoading) return <p className="nota-sec">Consultando o Tiny…</p>;
  if (cadastro.error) return <p className="erro-form">{(cadastro.error as Error).message}</p>;
  const d = cadastro.data!;
  if (!d.configurado)
    return <p className="nota-sec">O Tiny ainda não foi configurado no servidor.</p>;

  const st = STATUS[d.status];
  const botaoLink = conversaId && (
    <button className="btn" disabled={gerando} onClick={() => void gerarLink()}>
      <ClipboardList /> {gerando ? 'Gerando…' : 'Link de cadastro'}
    </button>
  );
  return (
    <div className="tiny">
      <div className="tiny-topo">
        <span className={`tiny-status ${st.classe}`}>{st.rotulo}</span>
        {d.contato && (
          <button
            className="icone-btn sem-borda"
            aria-label="Atualizar dados do Tiny"
            title="Atualizar dados do Tiny"
            disabled={atualizando}
            onClick={() => void atualizar()}
          >
            <RefreshCw className={atualizando ? 'girando' : ''} />
          </button>
        )}
      </div>

      {d.link?.estado === 'aguardando' && (
        <p className="tiny-link">
          Link de cadastro enviado em {new Date(d.link.em).toLocaleDateString('pt-BR')}. Esperando o
          cliente preencher.
        </p>
      )}
      {d.link?.estado === 'erro' && (
        <p className="tiny-link erro">
          O cliente preencheu, mas não foi pro Tiny: {d.link.erro}. Um admin pode reenviar em
          Configurações → Cadastros recebidos.
        </p>
      )}

      {d.contato ? (
        <>
          <dl className="kv">
            <dt>Nome</dt>
            <dd>{d.contato.nome}</dd>
            {d.contato.cpfCnpj && (
              <>
                <dt>{d.contato.tipoPessoa === 'J' ? 'CNPJ' : 'CPF'}</dt>
                <dd>{d.contato.cpfCnpj}</dd>
              </>
            )}
            {(d.contato.cidade || d.contato.uf) && (
              <>
                <dt>Cidade</dt>
                <dd>{[d.contato.cidade, d.contato.uf].filter(Boolean).join('/')}</dd>
              </>
            )}
            {d.contato.endereco && (
              <>
                <dt>Endereço</dt>
                <dd>
                  {d.contato.endereco}
                  {d.contato.cep ? ` · ${d.contato.cep}` : ''}
                </dd>
              </>
            )}
            {d.contato.email && (
              <>
                <dt>E-mail</dt>
                <dd>{d.contato.email}</dd>
              </>
            )}
          </dl>
          {d.faltando.length > 0 && (
            <div className="tiny-falta">
              <b>Falta pra emitir pedido e nota:</b>
              <ul>
                {d.faltando.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>
          )}
          {d.pedidos.length > 0 && (
            <div className="tiny-pedidos">
              <b>Últimos pedidos</b>
              <ul>
                {d.pedidos.map((p) => (
                  <li key={p.numero}>
                    <span>nº {p.numero}</span>
                    <small>{p.data}</small>
                    <span>{brl(p.valor)}</span>
                    <small>{p.situacao}</small>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="tiny-acoes">
            <button className="btn" onClick={() => setBuscando(true)}>
              <Link2 /> Trocar cadastro
            </button>
            <button className="btn fantasma" onClick={() => void desligar()}>
              <Unlink /> Desligar
            </button>
            {d.status === 'incompleto' && botaoLink}
          </div>
        </>
      ) : (
        <>
          <p className="nota-sec">Ainda não ligado a um cadastro do Tiny.</p>
          <div className="tiny-acoes">
            <button className="btn" onClick={() => setBuscando(true)}>
              <Search /> Buscar no Tiny
            </button>
            {botaoLink}
          </div>
        </>
      )}

      {buscando && (
        <ModalBuscaTiny
          contatoId={contatoId}
          fechar={() => setBuscando(false)}
          aoLigar={(novo) => {
            qc.setQueryData(chave, novo);
            setBuscando(false);
            toast('Contato ligado ao Tiny');
          }}
        />
      )}
    </div>
  );
}

function ModalBuscaTiny({
  contatoId,
  fechar,
  aoLigar,
}: {
  contatoId: string;
  fechar: () => void;
  aoLigar: (c: CadastroTinyDTO) => void;
}) {
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState<ContatoTinyResumoDTO[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [ligando, setLigando] = useState<string | null>(null);

  async function buscar(e: FormEvent) {
    e.preventDefault();
    setCarregando(true);
    setErro(null);
    try {
      setResultados(
        await api<ContatoTinyResumoDTO[]>(`/api/tiny/buscar?q=${encodeURIComponent(termo)}`),
      );
    } catch (err) {
      setResultados(null);
      setErro((err as Error).message);
    } finally {
      setCarregando(false);
    }
  }

  async function ligar(id: string) {
    setLigando(id);
    try {
      aoLigar(
        await api<CadastroTinyDTO>(`/api/contatos/${contatoId}/tiny`, {
          metodo: 'POST',
          corpo: { tinyId: id },
        }),
      );
    } catch (err) {
      setErro((err as Error).message);
    } finally {
      setLigando(null);
    }
  }

  return (
    <Modal titulo="Buscar cadastro no Tiny" aoFechar={fechar}>
      <form className="tiny-busca" onSubmit={buscar}>
        <input
          id="tiny-termo"
          className="input"
          autoFocus
          placeholder="CPF, CNPJ ou nome"
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
        />
        <button className="btn primario" disabled={carregando || !termo.trim()}>
          <Search /> {carregando ? 'Buscando…' : 'Buscar'}
        </button>
      </form>
      {erro && <p className="erro-form">{erro}</p>}
      {resultados &&
        (resultados.length === 0 ? (
          <p className="nota-sec">
            Nenhum cadastro encontrado. Confira o documento ou tente pelo nome.
          </p>
        ) : (
          <ul className="tiny-resultados">
            {resultados.map((r) => (
              <li key={r.id}>
                <span>
                  <b>{r.nome}</b>
                  <small>
                    {[
                      r.cpfCnpj && formatarDocumento(r.cpfCnpj),
                      [r.cidade, r.uf].filter(Boolean).join('/'),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                </span>
                <button className="btn" disabled={!!ligando} onClick={() => void ligar(r.id)}>
                  {ligando === r.id ? 'Ligando…' : 'Ligar'}
                </button>
              </li>
            ))}
          </ul>
        ))}
    </Modal>
  );
}
